/**
 * Single-source-of-truth state for HighLow.
 * Mirrors packages/amghighlow_v2/lib/presentation/bloc/game_bloc.dart.
 *
 * Single-player only. Tracks the 3-round cycle (currentRound 1..3) and the
 * 6-card slot array. The server-returned balance is deferred by the
 * orchestrator (pendingSettleBalance) until the win/lose result is shown,
 * then applied via the atomic settle() below (matches game_bloc.dart's
 * `pendingBalance` pattern).
 */
import type { BetType, GameConfig, GamePhase, GameRound } from '../types/game';
import { DEFAULT_GAME_CONFIG, ROUNDS_PER_CYCLE, CARDS_PER_CYCLE } from '../config';
import { BetCollection } from './betCollection';

export interface GameStateSnapshot {
  phase: GamePhase;
  balance: number;
  selectedChipIndex: number;
  bets: BetCollection;
  config: GameConfig;
  currentRound: number; // 1..ROUNDS_PER_CYCLE
  cards: ReadonlyArray<number | null>; // length CARDS_PER_CYCLE; null = not yet revealed
  lastRoundResult: GameRound | null;
  lastWinLose: number;
  errorMessage: string | null;
  isRiveReady: boolean;
}

type Listener = (snapshot: GameStateSnapshot, prev: GameStateSnapshot) => void;

export class GameState {
  private _phase: GamePhase = 'idle';
  private _balance = 0;
  private _selectedChipIndex = 0;
  private _bets: BetCollection = BetCollection.empty();
  private _config: GameConfig = { ...DEFAULT_GAME_CONFIG };
  private _currentRound = 1;
  private _cards: (number | null)[] = new Array(CARDS_PER_CYCLE).fill(null);
  private _lastRoundResult: GameRound | null = null;
  private _lastWinLose = 0;
  private _nextCycleFirstCard: number | null = null;
  private _errorMessage: string | null = null;
  private _isRiveReady = false;

  private listeners: Set<Listener> = new Set();

  snapshot(): GameStateSnapshot {
    return {
      phase: this._phase,
      balance: this._balance,
      selectedChipIndex: this._selectedChipIndex,
      bets: this._bets,
      config: this._config,
      currentRound: this._currentRound,
      cards: this._cards.slice(),
      lastRoundResult: this._lastRoundResult,
      lastWinLose: this._lastWinLose,
      errorMessage: this._errorMessage,
      isRiveReady: this._isRiveReady,
    };
  }

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Available balance = wallet balance − pending (uncommitted) bets. */
  get availableBalance(): number {
    return this._balance - this._bets.total;
  }

  get phase(): GamePhase {
    return this._phase;
  }
  get bets(): BetCollection {
    return this._bets;
  }
  get config(): GameConfig {
    return this._config;
  }
  get balance(): number {
    return this._balance;
  }
  get selectedChipIndex(): number {
    return this._selectedChipIndex;
  }
  get selectedChipValue(): number {
    return (
      this._config.chipValues[this._selectedChipIndex] ??
      this._config.chipValues[0]
    );
  }
  get currentRound(): number {
    return this._currentRound;
  }
  get cards(): ReadonlyArray<number | null> {
    return this._cards;
  }
  get lastWinLose(): number {
    return this._lastWinLose;
  }
  get lastRoundResult(): GameRound | null {
    return this._lastRoundResult;
  }
  get nextCycleFirstCard(): number | null {
    return this._nextCycleFirstCard;
  }

  // -----------------------------------------------------------------
  // Mutations — each emits a single change event
  // -----------------------------------------------------------------

  setPhase(p: GamePhase): void {
    if (p === this._phase) return;
    this.commit((s) => {
      s._phase = p;
    });
  }

  setRiveReady(v: boolean): void {
    if (v === this._isRiveReady) return;
    this.commit((s) => {
      s._isRiveReady = v;
    });
  }

  setBalance(b: number): void {
    if (b === this._balance) return;
    this.commit((s) => {
      s._balance = b;
    });
  }

  setConfig(c: GameConfig): void {
    this.commit((s) => {
      s._config = { ...c };
      if (s._selectedChipIndex >= c.chipValues.length) s._selectedChipIndex = 0;
    });
  }

  selectChip(i: number): void {
    const max = this._config.chipValues.length;
    if (i < 0 || i >= max || i === this._selectedChipIndex) return;
    this.commit((s) => {
      s._selectedChipIndex = i;
    });
  }

  /**
   * @returns true if accepted; false if rejected for balance/limits.
   *
   * Mirrors game_bloc.dart:230-241: total across ALL bet types must not
   * exceed config.maxBet (NOT per-area).
   */
  placeBet(bet: BetType, amount: number): boolean {
    if (this._phase !== 'betting') return false;
    if (amount <= 0) return false;
    if (this.availableBalance < amount) return false;
    if (this._bets.total + amount > this._config.maxBet) return false;

    this.commit((s) => {
      const next = s._bets.copy();
      next.add(bet, amount);
      s._bets = next;
    });
    return true;
  }

  clearBets(): void {
    if (this._bets.isEmpty) return;
    this.commit((s) => {
      s._bets = BetCollection.empty();
    });
  }

  /** Set the visible value of one card slot (1..6). */
  setCardSlot(slot: number, value: number | null): void {
    if (slot < 1 || slot > CARDS_PER_CYCLE) return;
    const idx = slot - 1;
    if (this._cards[idx] === value) return;
    this.commit((s) => {
      s._cards = s._cards.slice();
      s._cards[idx] = value;
    });
  }

  /** Start a new round within the current cycle. */
  setCurrentRound(round: number): void {
    if (round < 1 || round > ROUNDS_PER_CYCLE) return;
    if (round === this._currentRound) return;
    this.commit((s) => {
      s._currentRound = round;
    });
  }

  /** Reset all 6 card slots to null (shuffle preamble). */
  resetCards(): void {
    this.commit((s) => {
      s._cards = new Array(CARDS_PER_CYCLE).fill(null);
    });
  }

  applyRoundResult(round: GameRound): void {
    this.commit((s) => {
      s._lastRoundResult = round;
      s._lastWinLose = round.totalWinLose;
    });
  }

  /**
   * Settle a finished round: set the wallet balance to the server's
   * post-settlement value AND clear the now-resolved bets in a SINGLE
   * commit. Doing both atomically avoids a one-frame flash of an
   * intermediate `balance - bets.total` value when the displayed balance
   * is the available balance (see GameOrchestrator.applySettlement).
   */
  settle(balance: number): void {
    this.commit((s) => {
      s._balance = balance;
      s._bets = BetCollection.empty();
    });
  }

  setNextCycleFirstCard(card: number | null): void {
    if (this._nextCycleFirstCard === card) return;
    this.commit((s) => {
      s._nextCycleFirstCard = card;
    });
  }

  /** Wipe per-round transient state (bets cleared, result panel hidden). */
  resetForNewRound(): void {
    this.commit((s) => {
      s._bets = BetCollection.empty();
      s._lastRoundResult = null;
      s._lastWinLose = 0;
      s._errorMessage = null;
    });
  }

  setError(message: string | null): void {
    if (message === this._errorMessage) return;
    this.commit((s) => {
      s._errorMessage = message;
      if (message !== null) s._phase = 'error';
    });
  }

  private commit(mutator: (s: GameState) => void): void {
    const prev = this.snapshot();
    mutator(this);
    const next = this.snapshot();
    for (const l of this.listeners) {
      try {
        l(next, prev);
      } catch (e) {
        console.error('[GameState listener]', e);
      }
    }
  }
}
